import React from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Sparkles, Loader2, ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import AIQuotaBadge from "@/components/AIQuotaBadge";

/**
 * Renders an AI-generated section (Explain This Market, Opportunity Narrative,
 * Daily Brief). v2.5C: always appends a "Recommended Investigation" list when
 * present on the report. Every AI-facing surface now ends the same way.
 */
export default function AIReport({
  title = "AI report",
  report,
  onGenerate,
  isGenerating,
  ctaLabel = "Generate",
  testid = "ai-report",
  quotaEndpoint = null,
}) {
  const empty = !report;
  const recs = report?.recommended_investigation || [];
  return (
    <section className="border border-zinc-800 rounded-md p-4" data-testid={testid}>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-2">
        <div className="flex flex-wrap items-center gap-2">
          <Sparkles size={14} className="text-zinc-400" />
          <h3 className="font-heading font-semibold text-zinc-100">{title}</h3>
          {report?.fallback && (
            <span className="text-[10px] uppercase tracking-widest text-amber-300">
              provider unavailable · deterministic fallback
            </span>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {quotaEndpoint && <AIQuotaBadge endpoint={quotaEndpoint} compact />}
          <Button
            onClick={onGenerate}
            disabled={isGenerating}
            size="sm"
            data-testid={`${testid}-btn-generate`}
            className="h-7 text-xs bg-zinc-800 hover:bg-zinc-700 text-zinc-100"
          >
            {isGenerating ? (<><Loader2 size={12} className="mr-1 animate-spin" /> Generating…</>) :
                            (empty ? ctaLabel : "Regenerate")}
          </Button>
        </div>
      </div>
      {empty ? (
        <div className="text-xs text-zinc-500 py-2">
          Click <span className="text-zinc-300">{ctaLabel}</span> to summarize the evidence with Claude Sonnet 4.5.
        </div>
      ) : (
        <>
          <pre className="whitespace-pre-wrap font-sans text-sm text-zinc-300 leading-relaxed"
               data-testid={`${testid}-text`}>{report.text}</pre>

          {recs.length > 0 && (
            <div className="mt-4 pt-3 border-t border-zinc-800/70"
                 data-testid={`${testid}-recommended-section`}>
              <div className="text-[10px] uppercase tracking-[0.25em] text-blue-300/70 mb-2">
                Recommended Investigation
              </div>
              <ul className="space-y-1.5" data-testid={`${testid}-recommended`}>
                {recs.map((r, i) => (
                  <li key={i} className="flex gap-2 text-sm text-zinc-300 leading-snug">
                    <ChevronRight size={13} className="text-blue-400 mt-0.5 shrink-0" />
                    <span>{r}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <div className="mt-3 pt-2 border-t border-zinc-800 flex flex-wrap items-center gap-2 text-[10px] text-zinc-500 font-mono-num">
            <span>model {report.model}</span>
            <span>·</span>
            <span>prompt {report.prompt_version}</span>
            <span>·</span>
            <span>{new Date(report.generated_at).toLocaleString()}</span>
            <span>·</span>
            <span>{report.evidence_ids?.length ?? 0} evidence records</span>
          </div>
        </>
      )}
    </section>
  );
}

export function useAIReport(endpoint, key) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post(endpoint)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: [key] }),
  });
}
